import { useCallback, useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  FileSignature,
  PenLine,
  Loader2,
  CheckCircle2,
  Clock3,
  XCircle,
  MessageSquarePlus,
  CalendarOff,
  Paperclip,
  Upload,
  FileDown,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatMonthYear } from "@/lib/format";
import { openAuthFile } from "@/lib/files";
import {
  WarningSignDialog,
  downloadWarningPdf,
  WARNING_TYPE_LABEL,
} from "@/components/WarningsPanel";

const PUNCH_LABEL = {
  entry: "Entrada",
  break_start: "Saída intervalo",
  break_end: "Volta intervalo",
  exit: "Saída",
};
const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const REQ_BADGE = {
  pending: ["bg-amber-50 text-amber-800 border-amber-200", "Pendente"],
  approved: ["bg-emerald-50 text-emerald-800 border-emerald-200", "Aprovada"],
  rejected: ["bg-rose-50 text-rose-800 border-rose-200", "Recusada"],
};
const ABS_TYPES = [
  { value: "medical", label: "Atestado médico" },
  { value: "license", label: "Licença" },
  { value: "other", label: "Outro" },
];
const ABS_LABEL = Object.fromEntries(ABS_TYPES.map((t) => [t.value, t.label]));
const EMPTY_ABS = { date_from: "", date_to: "", leave_type: "medical", reason: "", attachment_url: "" };

const fmtMin = (m) => {
  if (m == null) return "—";
  const sign = m < 0 ? "-" : "";
  const a = Math.abs(Math.round(m));
  return `${sign}${Math.floor(a / 60)}h${String(a % 60).padStart(2, "0")}`;
};
const monthNow = () => new Date().toLocaleDateString("sv-SE").slice(0, 7);

const Stat = ({ label, value, tone = "" }) => (
  <div className="text-center">
    <div className="text-[10px] uppercase tracking-wide text-stone-500 font-body">{label}</div>
    <div className={`font-display font-bold ${tone}`}>{value}</div>
  </div>
);

export default function TimeMirror() {
  const [month, setMonth] = useState(monthNow());
  const [sheet, setSheet] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [reqDialog, setReqDialog] = useState(null); // {date}
  const [reqForm, setReqForm] = useState({ type: "entry", time: "", reason: "" });
  const [absOpen, setAbsOpen] = useState(false);
  const [absForm, setAbsForm] = useState(EMPTY_ABS);
  const [absUploading, setAbsUploading] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [signingWarning, setSigningWarning] = useState(null);

  const loadWarnings = async () => {
    try {
      const { data } = await api.get("/timeclock/warnings");
      setWarnings(data);
    } catch (err) {
      console.debug("warnings load failed (opcional)", err);
    }
  };

  useEffect(() => {
    loadWarnings();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: r }] = await Promise.all([
        api.get("/timeclock/my-timesheet", { params: { month } }),
        api.get("/timeclock/adjustment-requests", { params: { month } }),
      ]);
      setSheet(s);
      setRequests(r);
    } catch (err) {
      console.error("mirror load failed", err);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const openRequest = (d) => {
    setReqDialog(d);
    setReqForm({ type: "entry", time: "", reason: "" });
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    try {
      await api.post("/timeclock/adjustment-requests", {
        local_date: reqDialog.date,
        type: reqForm.type,
        requested_time: reqForm.time,
        reason: reqForm.reason,
      });
      toast.success("Solicitação enviada ao RH");
      setReqDialog(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar solicitação");
    }
  };

  const openAbsence = (dateStr) => {
    setAbsForm({ ...EMPTY_ABS, date_from: dateStr || "", date_to: dateStr || "" });
    setAbsOpen(true);
  };

  const uploadAttachment = async (file) => {
    if (!file) return;
    setAbsUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload?purpose=atestado", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAbsForm((f) => ({ ...f, attachment_url: data.url }));
      toast.success("Anexo enviado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no upload do anexo");
    } finally {
      setAbsUploading(false);
    }
  };

  const submitAbsence = async (e) => {
    e.preventDefault();
    try {
      await api.post("/timeclock/adjustment-requests", {
        kind: "absence",
        local_date: absForm.date_from,
        date_to: absForm.date_to || absForm.date_from,
        leave_type: absForm.leave_type,
        reason: absForm.reason,
        attachment_url: absForm.attachment_url,
      });
      toast.success("Justificativa enviada ao RH");
      setAbsOpen(false);
      setAbsForm(EMPTY_ABS);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar justificativa");
    }
  };

  const sign = async () => {
    setSigning(true);
    try {
      const { data } = await api.post("/timeclock/mirror/sign", { month });
      toast.success("Espelho assinado digitalmente");
      setSheet((s) => ({ ...s, signature: data }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao assinar");
    } finally {
      setSigning(false);
    }
  };

  const t = sheet?.totals;
  const signature = sheet?.signature;

  const downloadPdf = async () => {
    try {
      const resp = await api.get("/timeclock/timesheet/pdf", {
        params: { month },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `espelho_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF do espelho gerado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao gerar PDF");
    }
  };

  return (
    <Layout title="Espelho de Ponto" subtitle="Confira suas marcações, solicite ajustes e assine o mês">
      <div className="max-w-4xl mx-auto space-y-5" data-testid="mirror-page">
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs">Mês</Label>
            <Input
              type="month"
              value={month}
              max={monthNow()}
              onChange={(e) => setMonth(e.target.value)}
              className="mt-1 bg-white w-44"
              data-testid="mirror-month-filter"
            />
          </div>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-stone-400 mb-2.5" />}
          <div className="flex-1" />
          <Button
            variant="outline"
            className="bg-white border-stone-300"
            onClick={downloadPdf}
            data-testid="mirror-pdf-btn"
          >
            <FileDown className="h-4 w-4 mr-2" /> PDF do espelho
          </Button>
          <Button
            variant="outline"
            className="bg-white border-indigo-200 text-indigo-800 hover:bg-indigo-50"
            onClick={() => openAbsence("")}
            data-testid="absence-justify-btn"
          >
            <CalendarOff className="h-4 w-4 mr-2" /> Justificar falta
          </Button>
        </div>

        {t && (
          <Card className="rounded-xl border-stone-200 bg-white p-4">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              <Stat label="Trabalhado" value={fmtMin(t.worked_effective)} />
              <Stat label="Previsto" value={fmtMin(t.expected_minutes)} />
              <Stat label="Extra 50%" value={fmtMin(t.overtime_50)} />
              <Stat label="Extra 100%" value={fmtMin(t.overtime_100)} />
              <Stat label="Ad. noturno" value={fmtMin(t.night_additional_minutes)} />
              <Stat
                label="Saldo mês"
                value={fmtMin(t.balance_minutes)}
                tone={t.balance_minutes < 0 ? "text-rose-700" : "text-emerald-800"}
              />
            </div>
            {sheet.bank_balance != null && (
              <div className="text-xs text-stone-500 font-body text-center mt-3">
                Banco de horas acumulado:{" "}
                <span
                  className={`font-display font-bold ${
                    sheet.bank_balance < 0 ? "text-rose-700" : "text-emerald-800"
                  }`}
                >
                  {fmtMin(sheet.bank_balance)}
                </span>
              </div>
            )}
          </Card>
        )}

        {/* Advertências do colaborador */}
        {warnings.length > 0 && (
          <Card className="rounded-xl border-rose-200 bg-white p-4" data-testid="mirror-warnings">
            <div className="font-display font-bold text-sm mb-1 text-rose-800">
              Advertências disciplinares
            </div>
            <p className="text-xs text-stone-500 font-body mb-3">
              Assine para registrar a ciência (a assinatura não implica concordância).
            </p>
            <div className="space-y-2">
              {warnings.map((w) => (
                <div
                  key={w.id}
                  className="flex items-center gap-3 flex-wrap border border-stone-200 rounded-md px-3 py-2"
                  data-testid={`mirror-warning-${w.id}`}
                >
                  <Badge
                    variant="outline"
                    className={
                      w.warning_type === "falta"
                        ? "bg-rose-50 text-rose-800 border-rose-200 text-[10px]"
                        : "bg-amber-50 text-amber-800 border-amber-200 text-[10px]"
                    }
                  >
                    {WARNING_TYPE_LABEL[w.warning_type] || w.warning_type}
                  </Badge>
                  <span className="font-body text-sm flex-1 min-w-40">
                    Período {String(w.month).split("-").reverse().join("/")} · emitida por{" "}
                    {w.created_by_name}
                  </span>
                  {w.status === "signed" ? (
                    <Badge className="bg-emerald-600 text-white border-none text-[10px]">
                      Ciência registrada
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      className="bg-rose-700 hover:bg-rose-800 text-white"
                      onClick={() => setSigningWarning(w)}
                      data-testid={`mirror-warning-sign-${w.id}`}
                    >
                      <PenLine className="h-3.5 w-3.5 mr-1" /> Ler e assinar
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-white"
                    onClick={() => downloadWarningPdf(w)}
                    data-testid={`mirror-warning-pdf-${w.id}`}
                  >
                    <FileDown className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Signature */}
        {signature ? (
          <Card
            className="rounded-xl border-emerald-200 bg-emerald-50 p-4"
            data-testid="mirror-signature-receipt"
          >
            <div className="flex items-center gap-2 font-display font-bold text-emerald-900">
              <CheckCircle2 className="h-4 w-4" /> Espelho de {formatMonthYear(month)} assinado digitalmente
            </div>
            <div className="text-xs text-stone-600 font-body mt-1.5 space-y-0.5">
              <div>
                Assinado em{" "}
                {new Date(signature.signed_at).toLocaleString("pt-BR", {
                  dateStyle: "short",
                  timeStyle: "medium",
                })}{" "}
                · IP {signature.ip_address || "—"}
              </div>
              <div className="font-mono text-[10px] break-all" title={signature.hash}>
                Hash: {signature.hash}
              </div>
            </div>
          </Card>
        ) : (
          <Card className="rounded-xl border-stone-200 bg-white p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-start gap-2">
              <FileSignature className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
              <div>
                <div className="font-display font-bold text-sm">Assinatura do espelho</div>
                <p className="text-xs text-stone-500 font-body max-w-md">
                  Ao assinar, declaro que confiro as marcações e apurações deste espelho de ponto.
                  Serão registrados data/hora, IP e um hash de verificação.
                </p>
              </div>
            </div>
            <Button
              onClick={sign}
              disabled={signing || !sheet}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="mirror-sign-btn"
            >
              {signing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <PenLine className="h-4 w-4 mr-2" />
              )}
              Assinar espelho de {formatMonthYear(month)}
            </Button>
          </Card>
        )}

        {/* Daily mirror */}
        <Card className="rounded-xl border-stone-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="font-body">Data</TableHead>
                <TableHead className="font-body">Batidas</TableHead>
                <TableHead className="font-body">Trabalhado</TableHead>
                <TableHead className="font-body">Previsto</TableHead>
                <TableHead className="font-body">Saldo</TableHead>
                <TableHead className="font-body">Situação</TableHead>
                <TableHead className="font-body text-right">Ajuste</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="mirror-days">
              {(sheet?.days || []).map((d) => (
                <TableRow key={d.date} className={d.absent ? "bg-rose-50/40" : ""} data-testid={`mirror-day-${d.date}`}>
                  <TableCell className="font-body text-sm tabular-nums">
                    {d.date.slice(8)}/{d.date.slice(5, 7)}{" "}
                    <span className="text-stone-400 text-xs">{WEEKDAYS[d.weekday]}</span>
                  </TableCell>
                  <TableCell className="font-body text-xs tabular-nums">
                    {["entry", "break_start", "break_end", "exit"]
                      .map((k) => d.punches[k])
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </TableCell>
                  <TableCell className="font-body text-sm tabular-nums">
                    {d.worked_minutes ? fmtMin(d.worked_effective) : "—"}
                  </TableCell>
                  <TableCell className="font-body text-sm tabular-nums">
                    {d.expected_minutes ? fmtMin(d.expected_minutes) : "—"}
                  </TableCell>
                  <TableCell
                    className={`font-body text-sm font-semibold tabular-nums ${
                      d.balance_minutes < 0
                        ? "text-rose-700"
                        : d.balance_minutes > 0
                        ? "text-emerald-800"
                        : "text-stone-400"
                    }`}
                  >
                    {d.workday || d.worked_minutes ? fmtMin(d.balance_minutes) : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {d.is_holiday && (
                        <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-200 text-[9px]">
                          Feriado
                        </Badge>
                      )}
                      {d.leave_type && (
                        <Badge variant="outline" className="bg-indigo-50 text-indigo-800 border-indigo-200 text-[9px]">
                          Afastamento
                        </Badge>
                      )}
                      {d.absent && (
                        <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-[9px]">
                          Falta
                        </Badge>
                      )}
                      {d.records_count > 0 && !d.complete && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[9px]">
                          Incompleto
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-0.5">
                      {d.absent && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Justificar falta"
                          onClick={() => openAbsence(d.date)}
                          data-testid={`mirror-absence-btn-${d.date}`}
                        >
                          <CalendarOff className="h-4 w-4 text-indigo-600" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Solicitar ajuste de batida"
                        onClick={() => openRequest(d)}
                        data-testid={`mirror-request-btn-${d.date}`}
                      >
                        <MessageSquarePlus className="h-4 w-4 text-stone-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* My requests */}
        <Card className="rounded-xl border-stone-200 bg-white p-4">
          <div className="font-display font-bold text-sm mb-3">Minhas solicitações de ajuste</div>
          {requests.length === 0 ? (
            <div className="text-sm text-stone-500 font-body text-center py-3" data-testid="mirror-requests-empty">
              Nenhuma solicitação neste mês.
            </div>
          ) : (
            <div className="space-y-2" data-testid="mirror-requests-list">
              {requests.map((r) => {
                const [cls, label] = REQ_BADGE[r.status] || REQ_BADGE.pending;
                return (
                  <div
                    key={r.id}
                    className="flex items-center gap-3 border border-stone-200 rounded-lg px-3 py-2 flex-wrap"
                    data-testid={`mirror-request-${r.id}`}
                  >
                    {r.status === "pending" ? (
                      <Clock3 className="h-4 w-4 text-amber-600 shrink-0" />
                    ) : r.status === "approved" ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0" />
                    ) : (
                      <XCircle className="h-4 w-4 text-rose-600 shrink-0" />
                    )}
                    <div className="flex-1 min-w-48">
                      <div className="font-body text-sm">
                        {r.kind === "absence" ? (
                          <>
                            Justificativa de falta · {r.local_date.split("-").reverse().join("/")}
                            {r.date_to && r.date_to !== r.local_date
                              ? ` até ${r.date_to.split("-").reverse().join("/")}`
                              : ""}{" "}
                            · <span className="font-semibold">{ABS_LABEL[r.leave_type] || "Outro"}</span>
                          </>
                        ) : (
                          <>
                            {r.local_date.split("-").reverse().join("/")} · {PUNCH_LABEL[r.type]} às{" "}
                            <span className="font-semibold tabular-nums">{r.requested_time}</span>
                          </>
                        )}
                      </div>
                      <div className="text-xs text-stone-500 font-body">
                        {r.reason}
                        {r.resolution_note ? ` — RH: ${r.resolution_note}` : ""}
                      </div>
                      {r.attachment_url && (
                        <button
                          type="button"
                          onClick={() => openAuthFile(r.attachment_url)}
                          className="inline-flex items-center gap-1 text-xs text-emerald-800 underline underline-offset-2 mt-0.5"
                          data-testid={`mirror-request-attachment-${r.id}`}
                        >
                          <Paperclip className="h-3 w-3" /> Ver anexo
                        </button>
                      )}
                    </div>
                    <Badge variant="outline" className={`${cls} text-[10px]`}>
                      {label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Request dialog */}
      <Dialog
        open={!!reqDialog}
        onOpenChange={(o) => {
          if (!o) {
            setReqDialog(null);
            setReqForm({ type: "entry", time: "", reason: "" });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              Solicitar ajuste — {reqDialog ? reqDialog.date.split("-").reverse().join("/") : ""}
            </DialogTitle>
            <DialogDescription className="font-body text-xs">
              O RH vai analisar e, se aprovado, a batida é incluída/corrigida automaticamente.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRequest} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Batida</Label>
                <Select value={reqForm.type} onValueChange={(v) => setReqForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="request-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PUNCH_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Horário correto *</Label>
                <Input
                  type="time"
                  value={reqForm.time}
                  onChange={(e) => setReqForm((f) => ({ ...f, time: e.target.value }))}
                  required
                  className="mt-1 bg-white"
                  data-testid="request-time-input"
                />
              </div>
            </div>
            <div>
              <Label>Motivo *</Label>
              <Input
                value={reqForm.reason}
                onChange={(e) => setReqForm((f) => ({ ...f, reason: e.target.value }))}
                required
                placeholder="Ex: esqueci de bater na saída"
                className="mt-1 bg-white"
                data-testid="request-reason-input"
              />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="request-save-btn">
                Enviar solicitação
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Absence justification dialog — Fase E */}
      <Dialog
        open={absOpen}
        onOpenChange={(o) => {
          setAbsOpen(o);
          if (!o) setAbsForm(EMPTY_ABS);
        }}
      >
        <DialogContent data-testid="absence-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Justificar falta</DialogTitle>
            <DialogDescription className="font-body text-xs">
              Informe o período, o motivo e anexe o atestado (foto ou PDF). Se aprovado pelo RH,
              o afastamento é lançado automaticamente e os dias deixam de contar como falta.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAbsence} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>De *</Label>
                <Input
                  type="date"
                  value={absForm.date_from}
                  onChange={(e) =>
                    setAbsForm((f) => ({
                      ...f,
                      date_from: e.target.value,
                      date_to: f.date_to && f.date_to >= e.target.value ? f.date_to : e.target.value,
                    }))
                  }
                  required
                  className="mt-1 bg-white"
                  data-testid="absence-from-input"
                />
              </div>
              <div>
                <Label>Até *</Label>
                <Input
                  type="date"
                  value={absForm.date_to}
                  min={absForm.date_from || undefined}
                  onChange={(e) => setAbsForm((f) => ({ ...f, date_to: e.target.value }))}
                  required
                  className="mt-1 bg-white"
                  data-testid="absence-to-input"
                />
              </div>
            </div>
            <div>
              <Label>Tipo</Label>
              <Select
                value={absForm.leave_type}
                onValueChange={(v) => setAbsForm((f) => ({ ...f, leave_type: v }))}
              >
                <SelectTrigger className="mt-1 bg-white" data-testid="absence-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ABS_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Motivo *</Label>
              <Input
                value={absForm.reason}
                onChange={(e) => setAbsForm((f) => ({ ...f, reason: e.target.value }))}
                required
                placeholder="Ex: consulta médica com atestado"
                className="mt-1 bg-white"
                data-testid="absence-reason-input"
              />
            </div>
            <div>
              <Label>Atestado / comprovante (foto ou PDF)</Label>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <label
                  className="inline-flex items-center gap-2 border border-stone-300 rounded-md px-3 py-2 text-sm font-body bg-white cursor-pointer hover:bg-stone-50"
                  data-testid="absence-file-label"
                >
                  {absUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 text-stone-500" />
                  )}
                  {absForm.attachment_url ? "Trocar anexo" : "Enviar anexo"}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => uploadAttachment(e.target.files?.[0])}
                    data-testid="absence-file-input"
                  />
                </label>
                {absForm.attachment_url && (
                  <span
                    className="inline-flex items-center gap-1 text-xs text-emerald-800 font-body"
                    data-testid="absence-attachment-ok"
                  >
                    <Paperclip className="h-3 w-3" /> Anexo pronto
                  </span>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={absUploading}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="absence-save-btn"
              >
                Enviar justificativa
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <WarningSignDialog
        warning={signingWarning}
        open={!!signingWarning}
        onOpenChange={(o) => !o && setSigningWarning(null)}
        onSigned={loadWarnings}
      />
    </Layout>
  );
}
